import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { FieldEditor } from './FieldEditor'
import { ElementDefinition, FieldDefinition } from './useElementDefinitions'

interface ElementEditorProps {
    element: ElementDefinition
    onChange: (updated: ElementDefinition) => void
}

export const ElementEditor = ({ element, onChange }: ElementEditorProps): JSX.Element => {
    const [initialDataError, setInitialDataError] = useState<string | null>(null)

    const updateField = (partial: Partial<ElementDefinition>) => {
        onChange({ ...element, ...partial })
    }

    const handleInitialDataChange = (value: string) => {
        try {
            const parsed = JSON.parse(value)
            setInitialDataError(null)
            updateField({ initialData: parsed })
        } catch {
            setInitialDataError('Invalid JSON')
            // Still store the raw text so the user can keep editing
            // We do this by storing the raw string temporarily
            updateField({ initialData: value })
        }
    }

    const initialDataString =
        typeof element.initialData === 'string' ? element.initialData : JSON.stringify(element.initialData, null, 2)

    const updateFieldDefinition = (index: number, updated: FieldDefinition) => {
        const fields = [...element.fields]
        fields[index] = updated
        updateField({ fields })
    }

    const addField = () => {
        updateField({
            fields: [...element.fields, { type: 'text', label: '', valueName: '' }],
        })
    }

    const removeField = (index: number) => {
        updateField({ fields: element.fields.filter((_, i) => i !== index) })
    }

    return (
        <Box p={2}>
            <Typography variant="h6" mb={2}>
                Edit Element
            </Typography>
            <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                    <TextField
                        label="Name"
                        value={element.name}
                        onChange={(e) => updateField({ name: e.target.value })}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Identifier"
                        value={element.identifier}
                        onChange={(e) => updateField({ identifier: e.target.value })}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12}>
                    <TextField
                        label="AI Description"
                        value={element.aiDescription ?? ''}
                        onChange={(e) => updateField({ aiDescription: e.target.value || undefined })}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12}>
                    <TextField
                        label="Initial Data (JSON)"
                        value={initialDataString}
                        onChange={(e) => handleInitialDataChange(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={10}
                        error={!!initialDataError}
                        helperText={initialDataError ?? ''}
                        variant="filled"
                    />
                </Grid>
            </Grid>
            <Typography variant="subtitle1" mb={1}>
                Fields
            </Typography>
            {element.fields.map((field, i) => (
                <FieldEditor
                    key={i}
                    field={field}
                    onChange={(updated) => updateFieldDefinition(i, updated)}
                    onDelete={() => removeField(i)}
                />
            ))}
            <Button startIcon={<AddIcon />} onClick={addField} variant="outlined" size="small" sx={{ mt: 1 }}>
                Add Field
            </Button>
        </Box>
    )
}
